import LivenessSessionStatus from "../domain/LivenessSessionStatus.js"
import type { DisplayText } from "./dto/DisplayText.js"

/**
 * @description
 *	Base dictionary for the Spanish language, written in neutral Latin
 *	American Spanish, in tuteo. Every Spanish locale inherits it as is: the
 *	integration chooses the locale when it creates the session, and a single
 *	integration serves users of several countries with one locale, so a
 *	regional sentence would reach all of them.
 */
const es: DisplayText = {
	a11yVideoLabelText: "Cámara para la prueba de vida",
	cameraMinSpecificationsHeadingText: "La cámara no cumple con los requisitos mínimos.",
	cameraMinSpecificationsMessageText:
		"La cámara debe admitir una resolución de al menos 320×240 y 15 cuadros por segundo.",
	cameraNotFoundHeadingText: "La cámara no es accesible.",
	cameraNotFoundMessageText:
		"Verifica que tu cámara esté conectada y que no esté siendo utilizada por otra aplicación. Puede que tengas que ir a la configuración de tu dispositivo para otorgarnos permiso, cerrar tu navegador y reintentarlo otra vez.",
	cancelLivenessCheckText: "Cancelar la prueba de vida",
	clientHeaderText: "Error en el dispositivo",
	clientMessageText: "La verificación falló por un problema en el dispositivo.",
	connectionTimeoutHeaderText: "Se agotó el tiempo de conexión",
	connectionTimeoutMessageText: "La conexión no respondió a tiempo.",
	errorLabelText: "Error",
	faceDistanceHeaderText: "Detectamos un movimiento hacia la cámara",
	faceDistanceMessageText: "Evita acercarte mientras se conecta.",
	goodFitAltText: "Ilustración del rostro de una persona que encaja perfectamente dentro de un óvalo.",
	goodFitCaptionText: "¡Bien centrado!",
	hintCanNotIdentifyText: "Sitúa tu rostro frente a la cámara.",
	hintCenterFaceInstructionText:
		"Instrucción: antes de comenzar la verificación, asegúrate de que la cámara esté en la parte superior central de la pantalla y centra tu rostro frente a ella. Cuando comience la verificación, aparecerá un óvalo en el centro. Se te pedirá que te acerques hasta llenar el óvalo y luego que no te muevas. Después de unos segundos sin moverte, deberías escuchar «verificación completa».",
	hintCenterFaceText: "Centra tu rostro en el óvalo.",
	hintCheckCompleteText: "Verificación completa.",
	hintConnectingText: "Conectando...",
	hintFaceDetectedText: "¡Rostro detectado!",
	hintFaceOffCenterText: "Tu rostro está fuera del óvalo; céntralo frente a la cámara.",
	hintHoldFaceForFreshnessText: "¡No te muevas!",
	hintIlluminationNormalText: "Iluminación adecuada.",
	hintIlluminationTooBrightText: "Busca un lugar con menos luz.",
	hintIlluminationTooDarkText: "Busca un lugar con más luz.",
	hintMatchIndicatorText: "50% completo. Continúa acercándote más.",
	hintMoveFaceFrontOfCameraText: "Sitúa tu rostro frente a la cámara.",
	hintTooCloseText: "Aleja el rostro, estás muy cerca.",
	hintTooFarText: "Acércate más, estás muy lejos.",
	hintTooManyFacesText: "Asegúrate de que haya un solo rostro frente a la cámara.",
	hintVerifyingText: "Verificando resultados...",
	landscapeHeaderText: "La orientación horizontal no está soportada.",
	landscapeMessageText: "Gira tu dispositivo hacia la orientación vertical.",
	multipleFacesHeaderText: "Detectamos otros rostros; debes estar solo.",
	multipleFacesMessageText: "Asegúrate de que haya un solo rostro frente a la cámara mientras se conecta.",
	photosensitivityWarningBodyText:
		"La verificación muestra luces de colores y parpadeos. Ten cuidado si eres fotosensible.",
	photosensitivityWarningHeadingText: "Advertencia de fotosensibilidad",
	photosensitivityWarningInfoText:
		"Algunas personas pueden experimentar convulsiones epilépticas cuando son expuestas a luces de colores y parpadeos. Ten cuidado si tú, o alguien de tu familia, sufre de epilepsia.",
	photosensitivityWarningLabelText: "Más información sobre la fotosensibilidad...",
	photosensitivyWarningBodyText:
		"La verificación muestra luces de colores y parpadeos. Ten cuidado si eres fotosensible.",
	photosensitivyWarningHeadingText: "Advertencia de fotosensibilidad",
	photosensitivyWarningInfoText:
		"Algunas personas pueden experimentar convulsiones epilépticas cuando son expuestas a luces de colores y parpadeos. Ten cuidado si tú, o alguien de tu familia, sufre de epilepsia.",
	photosensitivyWarningLabelText: "Más información sobre la fotosensibilidad...",
	portraitMessageText:
		"Asegúrate de que tu dispositivo permanezca en orientación vertical durante toda la verificación.",
	recordingIndicatorText: "REC",
	retryCameraPermissionsText: "Reintentarlo",
	serverHeaderText: "Hubo un error",
	serverMessageText: "No podemos completar la verificación en este preciso momento. Inténtalo nuevamente.",
	startScreenBeginCheckText: "Iniciar verificación",
	timeoutHeaderText: "Se acabó el tiempo",
	timeoutMessageText:
		"Tu rostro no se mantuvo en el óvalo según nuestras instrucciones dentro del tiempo límite. Inténtalo nuevamente, llenando el óvalo con tu rostro completamente.",
	tooFarAltText:
		"Ilustración del rostro de una persona dentro de un óvalo, con un espacio entre el contorno del rostro y el borde del óvalo.",
	tooFarCaptionText: "Estás demasiado lejos.",
	tryAgainText: "Inicia otra verificación",
	waitingCameraPermissionText: "Esperando a que habilites el permiso para usar tu cámara frontal...",

	// Biometry display text.
	canCloseAfterCompletion: "Ya puedes cerrar esta página.",
	faceLivenessDetectionCompletion: {
		[LivenessSessionStatus.EXPIRED]: "La sesión expiró; vas a tener que intentarlo de nuevo.",
		[LivenessSessionStatus.FAILED]: "La prueba de vida falló; vas a tener que intentarlo de nuevo.",
		[LivenessSessionStatus.SUCCEEDED]: "Estamos analizando tu prueba de vida."
	},
	faceSignatureCompletion: {
		[LivenessSessionStatus.EXPIRED]: "La sesión expiró; vas a tener que intentarlo de nuevo.",
		[LivenessSessionStatus.FAILED]: "La firma facial falló; vas a tener que intentarlo de nuevo.",
		[LivenessSessionStatus.SUCCEEDED]: "Estamos analizando tu firma facial."
	},
	landingProvidedBy: "Verificación provista por",
	landingStartButton: "Iniciar verificación",
	landingStepCamera: "Permite el acceso a la cámara",
	landingStepHold: "Quédate sin moverte unos segundos",
	landingStepOval: "Encuadra tu cara en el óvalo",
	landingSubtitle: "Confirmamos que eres tú con una prueba de vida. Tarda menos de un minuto.",
	landingTitle: "Verificación de identidad",
	photosensitiveWarningHeading: "Advertencia de fotosensibilidad",
	photosensitiveWarningText:
		"La verificación muestra luces de colores y parpadeos. Ten cuidado si eres fotosensible.",
	photosensitiveWarningInfo:
		"Algunas personas pueden experimentar convulsiones epilépticas cuando son expuestas a luces de colores y parpadeos. Ten cuidado si tú, o alguien de tu familia, sufre de epilepsia.",
	exitBtnText: "Volver",
	errorStates: {
		RUNTIME_ERROR: "ERROR",
		SERVER_ERROR: "ERROR",
		CAMERA_ACCESS_ERROR: "Error de acceso a la cámara",
		CAMERA_FRAMERATE_ERROR: "Error de cámara",
		FACE_DISTANCE_ERROR: "Error de distancia del rostro",
		MOBILE_LANDSCAPE_ERROR: "Error en orientación horizontal",
		TIMEOUT: "Tiempo de espera agotado",
		UNKNOWN_ERROR: "ERROR"
	},
	errorMessages: {
		ValidationException: "Sesión inválida. Por favor, solicita una nueva.",
		TIMEOUT: "Sesión expirada. Por favor, solicita una nueva.",
		default: "Ha ocurrido un error. Por favor, solicita una nueva sesión."
	}
}

export default es
